import { useMemo, useState } from 'react';
import {
  Card,
  Button,
  Badge,
  Tabs,
  LoadingSkeleton,
} from '@nexora/design-system';
import {
  useServiceOrder,
  SERVICE_ORDER_STATUS_LABEL,
  SERVICE_ORDER_STATUS_VARIANT,
  SERVICE_ORDER_PRIORITY_LABEL,
  SERVICE_ORDER_PRIORITY_VARIANT,
  SERVICE_ORDER_TYPE_LABEL,
  formatHours,
} from './useServiceOrders';
import { ServiceOrderPicker } from './ServiceOrderPicker';
import { formatCurrency, formatDate, formatDateTime } from '../../shared/utils/format';

interface ServiceOrderDetailPageProps {
  /** Id da OS. Se ausente, le `?os=` da URL; sem isso, mostra o seletor. */
  orderId?: string;
  /** Navegacao para as telas de acao (apontamento/materiais/historico). */
  onOpenSection?: (section: 'time-entries' | 'materials' | 'comments', id: string) => void;
}

const TABS = [
  { key: 'dados', label: 'Dados' },
  { key: 'horas', label: 'Horas' },
  { key: 'materiais', label: 'Materiais' },
  { key: 'historico', label: 'Histórico' },
];

/**
 * Detalhe agregado de uma Ordem de Servico — `GET /manufacturing/service-orders/:id`.
 *
 * O backend devolve a OS com `timeEntries`, `materials` e `comments` no mesmo
 * envelope; a pagina organiza em abas. As telas de acao (apontar horas,
 * adicionar material, comentar) vivem em paginas proprias — os botoes aqui
 * apenas navegam ate elas (o router injeta o destino).
 */
export function ServiceOrderDetailPage({
  orderId,
  onOpenSection,
}: ServiceOrderDetailPageProps): JSX.Element {
  /* Fonte do id: prop > query string (`?os=<uuid>`) > seletor. */
  const urlOrderId = useMemo(
    () => new URLSearchParams(window.location.search).get('os') ?? undefined,
    [],
  );
  const [pickedId, setPickedId] = useState<string | null>(null);
  const activeId = orderId ?? pickedId ?? urlOrderId;

  const { data: detail, isLoading, error, refetch } = useServiceOrder(activeId);

  const [activeTab, setActiveTab] = useState('dados');

  if (!activeId) {
    return (
      <div className="so-detail-page">
        <ServiceOrderPicker onSelect={setPickedId} label="Detalhes da Ordem de Serviço" />
      </div>
    );
  }

  const openSection = (section: 'time-entries' | 'materials' | 'comments'): void => {
    if (onOpenSection) {
      onOpenSection(section, activeId);
      return;
    }
    /* Caminho canonico que o roteamento futuro mapeia para a pagina da secao:
       `/manufacturing/service-orders/:id/time-entries`. O `?os=` permite a
       pagina receber o id mesmo antes do router injetar via prop/param. */
    window.location.assign(`/manufacturing/service-orders/${activeId}/${section}?os=${activeId}`);
  };

  return (
    <div className="so-detail-page">
      {/* ── Header ── */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <div className="flex items-center gap-sm mb-xs flex-wrap">
            <span className="font-label-md text-label-md text-on-surface-variant bg-surface-container-high px-2 py-1 rounded" style={{ background: '#eceef0', color: '#43474e' }}>
              {detail?.number ?? 'Carregando...'}
            </span>
            {detail && (
              <Badge variant={SERVICE_ORDER_STATUS_VARIANT[detail.status]} size="sm">
                {SERVICE_ORDER_STATUS_LABEL[detail.status]}
              </Badge>
            )}
            {detail && (
              <Badge variant={SERVICE_ORDER_PRIORITY_VARIANT[detail.priority]} size="sm" dot>
                {SERVICE_ORDER_PRIORITY_LABEL[detail.priority]}
              </Badge>
            )}
            {detail && (
              <Badge variant="neutral" size="sm">
                {SERVICE_ORDER_TYPE_LABEL[detail.type]}
              </Badge>
            )}
          </div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">{detail?.title ?? 'Ordem de Serviço'}</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            {detail?.description ?? 'Carregando detalhes da ordem...'}
          </p>
        </div>
        <div className="flex flex-wrap gap-sm">
          <Button size="sm" variant="outline" onClick={() => openSection('time-entries')}>Apontar horas</Button>
          <Button size="sm" variant="outline" onClick={() => openSection('materials')}>Materiais</Button>
          <Button size="sm" variant="outline" onClick={() => openSection('comments')}>Histórico</Button>
        </div>
      </div>

      {isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={6} height="48px" />
        </Card>
      )}

      {error && !isLoading && (
        <Card padding="2rem">
          <div className="state-error" role="alert">
            <p>Erro ao carregar a ordem de serviço</p>
            <p className="state-detail">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        </Card>
      )}

      {detail && !isLoading && !error && (
        <>
          {/* ── Resumo ── */}
          <div className="so-detail__stat-grid">
            <div className="so-detail__stat">
              <span className="so-stat-label">Horas apontadas</span>
              <span className="so-stat-value">{formatHours(detail.totalHours)}</span>
            </div>
            <div className="so-detail__stat">
              <span className="so-stat-label">Custo em materiais</span>
              <span className="so-stat-value">{formatCurrency(detail.totalMaterialCost)}</span>
            </div>
            <div className="so-detail__stat">
              <span className="so-stat-label">Cliente</span>
              <span className="so-stat-value">{detail.customerRef ?? '—'}</span>
            </div>
            <div className="so-detail__stat">
              <span className="so-stat-label">Equipamento</span>
              <span className="so-stat-value">{detail.equipmentRef ?? '—'}</span>
            </div>
            <div className="so-detail__stat">
              <span className="so-stat-label">Prev. início</span>
              <span className="so-stat-value">{detail.plannedStart ? formatDate(detail.plannedStart) : '—'}</span>
            </div>
            <div className="so-detail__stat">
              <span className="so-stat-label">Prev. fim</span>
              <span className="so-stat-value">{detail.plannedEnd ? formatDate(detail.plannedEnd) : '—'}</span>
            </div>
          </div>

          {/* ── Abas ── */}
          <Tabs
            tabs={TABS}
            value={activeTab}
            onChange={setActiveTab}
            variant="underline"
            aria-label="Abas da ordem de serviço"
          />

          <div className="mt-md">
            {activeTab === 'dados' && (
              <Card padding="1.25rem">
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0 mb-sm">Informações da OS</h3>
                <dl className="so-list">
                  <div><dt>Número</dt><dd>{detail.number}</dd></div>
                  <div><dt>Título</dt><dd>{detail.title}</dd></div>
                  <div><dt>Descrição</dt><dd>{detail.description ?? '—'}</dd></div>
                  <div><dt>Status</dt><dd>{SERVICE_ORDER_STATUS_LABEL[detail.status]}</dd></div>
                  <div><dt>Prioridade</dt><dd>{SERVICE_ORDER_PRIORITY_LABEL[detail.priority]}</dd></div>
                  <div><dt>Tipo</dt><dd>{SERVICE_ORDER_TYPE_LABEL[detail.type]}</dd></div>
                  <div><dt>Cliente</dt><dd>{detail.customerRef ?? '—'}</dd></div>
                  <div><dt>Equipamento</dt><dd>{detail.equipmentRef ?? '—'}</dd></div>
                  <div><dt>Início planejado</dt><dd>{detail.plannedStart ? formatDateTime(detail.plannedStart) : '—'}</dd></div>
                  <div><dt>Fim planejado</dt><dd>{detail.plannedEnd ? formatDateTime(detail.plannedEnd) : '—'}</dd></div>
                  <div><dt>Início real</dt><dd>{detail.actualStart ? formatDateTime(detail.actualStart) : '—'}</dd></div>
                  <div><dt>Fim real</dt><dd>{detail.actualEnd ? formatDateTime(detail.actualEnd) : '—'}</dd></div>
                  <div><dt>Total horas</dt><dd>{formatHours(detail.totalHours)}</dd></div>
                  <div><dt>Total materiais</dt><dd>{formatCurrency(detail.totalMaterialCost)}</dd></div>
                  <div><dt>Criada em</dt><dd>{formatDateTime(detail.createdAt)}</dd></div>
                  <div><dt>Atualizada em</dt><dd>{formatDateTime(detail.updatedAt)}</dd></div>
                </dl>
              </Card>
            )}

            {activeTab === 'horas' && (
              <Card padding="0">
                <CardHeaderTitle count={detail.timeEntries.length} singular="apontamento" plural="apontamentos" title="Apontamentos de horas" />
                <div className="so-table-wrap">
                  <table className="so-table">
                    <thead>
                      <tr>
                        <th>Colaborador</th>
                        <th style={{ textAlign: 'right' }}>Horas</th>
                        <th>Data</th>
                        <th>Descrição</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.timeEntries.length === 0 && (
                        <tr>
                          <td colSpan={4}>
                            <div className="state-empty">Nenhum apontamento de horas ainda. Use “Apontar horas” para registrar.</div>
                          </td>
                        </tr>
                      )}
                      {detail.timeEntries.map((t) => (
                        <tr key={t.id}>
                          <td>{t.employeeName}</td>
                          <td className="so-num">{formatHours(t.hours)}</td>
                          <td>{formatDateTime(t.occurredAt)}</td>
                          <td>{t.description ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {detail.timeEntries.length > 0 && (
                  <div className="so-table-footer">
                    <span className="so-page-info">
                      Soma: <strong>{formatHours(detail.timeEntries.reduce((a, t) => a + t.hours, 0))}</strong>
                    </span>
                  </div>
                )}
              </Card>
            )}

            {activeTab === 'materiais' && (
              <Card padding="0">
                <CardHeaderTitle count={detail.materials.length} singular="material" plural="materiais" title="Materiais e peças" />
                <div className="so-table-wrap">
                  <table className="so-table">
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th>Descrição</th>
                        <th style={{ textAlign: 'right' }}>Qtd</th>
                        <th>Unid.</th>
                        <th style={{ textAlign: 'right' }}>Custo un.</th>
                        <th style={{ textAlign: 'right' }}>Total</th>
                        <th>Usado em</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.materials.length === 0 && (
                        <tr>
                          <td colSpan={7}>
                            <div className="state-empty">Nenhum material registrado ainda. Use a página “Materiais” para adicionar.</div>
                          </td>
                        </tr>
                      )}
                      {detail.materials.map((m) => (
                        <tr key={m.id}>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{m.productId.slice(0, 8)}</td>
                          <td>{m.description ?? '—'}</td>
                          <td className="so-num">{m.quantity}</td>
                          <td>{m.unit}</td>
                          <td className="so-num">{formatCurrency(m.unitCost)}</td>
                          <td className="so-num">{formatCurrency(m.totalCost)}</td>
                          <td>{formatDate(m.usedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {detail.materials.length > 0 && (
                  <div className="so-table-footer">
                    <span className="so-page-info">
                      Soma: <strong>{formatCurrency(detail.materials.reduce((a, m) => a + m.totalCost, 0))}</strong>
                    </span>
                  </div>
                )}
              </Card>
            )}

            {activeTab === 'historico' && (
              <Card padding="1.25rem">
                <div className="flex items-center justify-between mb-md">
                  <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Linha do tempo da OS</h3>
                  <Button size="sm" variant="outline" onClick={() => openSection('comments')}>Adicionar comentário</Button>
                </div>
                {detail.comments.length === 0 && (
                  <div className="state-empty">Nenhum comentário registrado. O histórico começa quando alguém postar uma nota.</div>
                )}
                <div className="so-timeline">
                  {detail.comments.map((c) => (
                    <div key={c.id} className="so-timeline__item">
                      <div className="so-timeline__dot">💬</div>
                      <div className="so-timeline__body">
                        <div className="so-timeline__head">
                          <strong>{c.authorName}</strong>
                          <span className="so-timeline__date">{formatDateTime(c.createdAt)}</span>
                        </div>
                        <p className="m-0">{c.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </>
      )}

      <style>{`
        .so-detail-page { padding: 1.5rem; }
        .so-card-header { display: flex; align-items: center; justify-content: space-between; gap: 1rem; padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; }
        .so-detail__stat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 0.75rem; margin-bottom: 1.25rem; }
        .so-detail__stat { padding: 0.75rem; background: #f2f4f6; border-radius: 4px; text-align: center; }
        .so-stat-label { display: block; font-size: 12px; color: #43474e; margin-bottom: 0.25rem; font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 500; letter-spacing: 0.02em; }
        .so-stat-value { font-size: 0.9375rem; font-weight: 600; color: #191c1e; }
        .so-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 0.5rem 1.5rem; margin: 0; }
        .so-list > div { display: flex; gap: 0.5rem; border-bottom: 1px solid #eceef0; padding: 0.375rem 0; }
        .so-list dt { color: #74777f; font-size: 0.8125rem; min-width: 130px; }
        .so-list dd { margin: 0; color: #191c1e; font-size: 0.875rem; font-weight: 500; }
        .so-table-wrap { overflow-x: auto; }
        .so-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .so-table th {
          padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600;
          font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5; text-align: left; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .so-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; vertical-align: top; }
        .so-table tbody tr:hover td { background: #eceef0; }
        .so-num { text-align: right; font-family: 'JetBrains Mono', ui-monospace, monospace; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .so-table-footer { display: flex; justify-content: flex-end; padding: 0.75rem 1rem; border-top: 1px solid #eceef0; }
        .so-page-info { font-size: 0.8125rem; color: #43474e; }
        .so-timeline { position: relative; padding-left: 0.25rem; }
        .so-timeline__item { display: flex; gap: 0.75rem; margin-bottom: 1rem; }
        .so-timeline__dot { width: 32px; height: 32px; border-radius: 50%; background: #f2f4f6; border: 2px solid #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .so-timeline__body { flex: 1; background: #fafbfc; border: 1px solid #eceef0; border-radius: 8px; padding: 0.625rem 0.875rem; }
        .so-timeline__head { display: flex; justify-content: space-between; align-items: baseline; gap: 0.5rem; margin-bottom: 0.25rem; }
        .so-timeline__date { font-size: 0.75rem; color: #74777f; }
        .state-error { text-align: center; }
        .state-error p { margin: 0; color: #ba1a1a; font-weight: 600; }
        .state-error .state-detail { color: #74777f; font-size: 0.875rem; font-weight: 400; margin-top: 0.5rem; }
        .state-empty { text-align: center; padding: 1.5rem 0.5rem; color: #74777f; }
      `}</style>
    </div>
  );
}

/** Cabeçalho pequeno para os cartões de aba. */
function CardHeaderTitle({ title, count, singular, plural }: { title: string; count: number; singular: string; plural: string }): JSX.Element {
  return (
    <div className="so-card-header">
      <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">{title}</h3>
      <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
        {count} {count === 1 ? singular : plural}
      </span>
    </div>
  );
}